import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import { cn, formatMoney, formatNumber } from '@/platform/lib/utils';
import {
    Badge,
    Button,
    Dropdown,
    Input,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Toggle,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import { MinusIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { router, useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';
import BooleanIcon from './BooleanIcon';

const COLORS = {
    Hoog: 'red',
    Laag: 'green',
    Herzien: 'yellow',
    Combinatie: 'blue',
    Geen: 'grey',
};

const ContractCheckPeriodsTable = ({ data, contractCheck, year }) => {
    const { auth, types } = usePage().props;
    const {
        data: rangeData,
        setData,
        post,
        processing,
        reset,
    } = useForm({ from_date: '', to_date: '', reason: '' });

    const [pickedYear, setPickedYear] = useState(null);
    const [pending, setPending] = useState(null);

    const years = Object.keys(data?.data || {});
    const selectedYear = pickedYear ?? (year || years.at(-1)) ?? null;
    const periodData = data?.data?.[selectedYear] ?? [];

    const currentYear = new Date().getFullYear();
    const canGeneratePrognosis =
        Number(selectedYear) === currentYear &&
        periodData?.data?.at(-1)?.savings != null;

    const baseUrl = `/customers/${auth?.user?.selected_customer_id}/services/ww-check/contract-check/${contractCheck?.id}`;
    const options = {
        preserveScroll: true,
        only: ['employments', 'savings'],
    };

    // Tracks which request is running, so only that control shows as busy.
    const send = (key, method, url, payload = {}) => {
        setPending(key);
        router[method](url, payload, {
            ...options,
            onFinish: () => setPending(null),
        });
    };

    const storeRange = () =>
        post(`${baseUrl}/savings`, { ...options, onSuccess: () => reset() });

    const clearSavings = () => send('clear', 'delete', `${baseUrl}/savings`);

    const generatePrognosis = () =>
        send('prognosis', 'post', `${baseUrl}/projection`);

    const toggleSaving = (employmentPeriod, checked) =>
        checked
            ? send(
                  employmentPeriod.id,
                  'put',
                  `${baseUrl}/savings/${employmentPeriod.id}`
              )
            : send(
                  employmentPeriod.id,
                  'delete',
                  `${baseUrl}/savings/${employmentPeriod.id}`
              );

    const updateReason = (employmentPeriod, reason) =>
        send(
            employmentPeriod.id,
            'put',
            `${baseUrl}/savings/${employmentPeriod.id}`,
            {
                reason: reason || null,
            }
        );

    return (
        <>
            <div className='my-8 flex flex-col items-end justify-end gap-4 sm:flex-row'>
                <div className='flex items-center justify-end gap-4'>
                    <Input
                        type='date'
                        value={rangeData?.from_date || ''}
                        onChange={event =>
                            setData('from_date', event.target.value)
                        }
                    />
                    <Input
                        type='date'
                        value={rangeData?.to_date || ''}
                        onChange={event =>
                            setData('to_date', event.target.value)
                        }
                    />
                    <Dropdown
                        className='max-w-48'
                        options={types || []}
                        placeholder='Reden'
                        value={rangeData?.reason || ''}
                        onChange={value => setData('reason', value)}
                    />

                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionMarkCircleIcon className='size-5' />
                        </TooltipTrigger>
                        <TooltipContent>
                            U kunt een datumbereik opgeven waarop er wordt
                            bespaard.
                        </TooltipContent>
                    </Tooltip>
                </div>
                <div className='flex items-center justify-end gap-4'>
                    <Button
                        onClick={storeRange}
                        loading={processing}
                        variant='primary'
                        type='button'
                        disabled={!rangeData?.from_date || !rangeData?.reason}
                    >
                        Periodes wisselen
                    </Button>
                    <Button
                        onClick={clearSavings}
                        loading={pending === 'clear'}
                        type='button'
                    >
                        Reset alle periodes
                    </Button>
                    <Button
                        variant='danger'
                        type='button'
                        onClick={generatePrognosis}
                        disabled={!canGeneratePrognosis}
                        loading={pending === 'prognosis'}
                    >
                        Genereer een prognose
                    </Button>
                    <Tooltip>
                        <TooltipTrigger>
                            <QuestionMarkCircleIcon className='size-5' />
                        </TooltipTrigger>
                        <TooltipContent>
                            Je kan een prognose enkel genereren voor het jaar{' '}
                            {currentYear} én als er een besparing is in de
                            laatst beschikbare periode
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    Periode
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>
                                Periode (Maandnummer)
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    SrtIV
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>
                                Soort Inkomstenverhouding
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    CdAard
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>
                                Code aard arbeidsverhouding
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    OT
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>Onbepaalde tijd</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    SA
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>
                                Schriftelijk aangegaan
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    OC
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>Oproep Contract</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    C. uren
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>Contract uren</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <TableHeaderCell textAlign='center'>
                                    V. uren
                                </TableHeaderCell>
                            </TooltipTrigger>
                            <TooltipContent>Verloonde uren</TooltipContent>
                        </Tooltip>
                        <TableHeaderCell textAlign='center'>
                            Premieloon
                        </TableHeaderCell>
                        <TableHeaderCell textAlign='center'>
                            Premie
                        </TableHeaderCell>
                        <TableHeaderCell textAlign='center'>
                            Percentage
                        </TableHeaderCell>
                        <TableHeaderCell textAlign='center'>
                            Type
                        </TableHeaderCell>
                        <TableHeaderCell content='' textAlign='right'>
                            Mog. Besparing
                        </TableHeaderCell>
                        <TableHeaderCell textAlign='center'>
                            Is een besparing
                        </TableHeaderCell>
                        <TableHeaderCell collapsing textAlign='right'>
                            Reden
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!periodData?.data && <NoDataRow numberOfColumns={15} />}

                    {periodData?.data?.map(employmentPeriod => (
                        <TableRow
                            key={employmentPeriod.id}
                            error={employmentPeriod.is_projection}
                        >
                            <TableCell textAlign='center'>
                                {employmentPeriod?.period?.number}
                            </TableCell>
                            <TableCell textAlign='center'>
                                {employmentPeriod.income_relationship_type}
                            </TableCell>
                            <TableCell textAlign='center'>
                                {employmentPeriod.nature_of_employment}
                            </TableCell>
                            <TableCell textAlign='center'>
                                <BooleanIcon
                                    positive={
                                        employmentPeriod.contract_type ===
                                        'permanent'
                                    }
                                />
                            </TableCell>
                            <TableCell textAlign='center'>
                                <BooleanIcon
                                    positive={employmentPeriod.written_contract}
                                />
                            </TableCell>
                            <TableCell textAlign='center'>
                                <BooleanIcon
                                    positive={employmentPeriod.on_call_contract}
                                />
                            </TableCell>
                            <TableCell textAlign='center'>
                                {employmentPeriod.contract_hours}
                            </TableCell>
                            <TableCell textAlign='center'>
                                {employmentPeriod.paid_hours}
                                <span className='text-xs text-gray-400'>
                                    {' '}
                                    (
                                    {employmentPeriod.paid_hours_percentage > 0
                                        ? '+'
                                        : ''}
                                    {employmentPeriod.paid_hours_percentage}
                                    %)
                                </span>
                            </TableCell>
                            <TableCell textAlign='right'>
                                {formatMoney(employmentPeriod.premieloon_awf)}
                            </TableCell>
                            <TableCell textAlign='right'>
                                {formatMoney(employmentPeriod.premie_awf)}
                            </TableCell>
                            <TableCell textAlign='center'>
                                {employmentPeriod.premie_awf_percentage}%
                            </TableCell>
                            <TableCell textAlign='center'>
                                <Badge
                                    color={
                                        COLORS[employmentPeriod.premie_awf_type]
                                    }
                                >
                                    {employmentPeriod.premie_awf_type}
                                </Badge>
                            </TableCell>
                            <TableCell textAlign='right'>
                                {employmentPeriod.potential_savings !== null ? (
                                    formatMoney(
                                        employmentPeriod.potential_savings
                                    )
                                ) : (
                                    <MinusIcon className='inline-flex size-5' />
                                )}
                            </TableCell>
                            <TableCell textAlign='center'>
                                <Toggle
                                    checked={employmentPeriod.savings != null}
                                    disabled={pending === employmentPeriod.id}
                                    onChange={checked =>
                                        toggleSaving(employmentPeriod, checked)
                                    }
                                />
                            </TableCell>
                            <TableCell textAlign='right' collapsing>
                                <Dropdown
                                    className='max-w-36'
                                    value={
                                        employmentPeriod.savings?.reason || ''
                                    }
                                    options={types || []}
                                    placeholder='Reden'
                                    disabled={
                                        employmentPeriod.savings == null ||
                                        pending === employmentPeriod.id
                                    }
                                    onChange={value =>
                                        updateReason(employmentPeriod, value)
                                    }
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell colSpan={6} />
                        <TableCell textAlign='center'>
                            {periodData?.totals?.contract_hours}
                        </TableCell>
                        <TableCell textAlign='center'>
                            {periodData?.totals?.paid_hours}
                            <span className='text-xs text-gray-400'>
                                {' '}
                                (
                                {periodData?.totals?.paid_hours_percentage > 0
                                    ? '+'
                                    : ''}
                                {periodData?.totals?.paid_hours_percentage}
                                %)
                            </span>
                        </TableCell>
                        <TableCell textAlign='right'>
                            {formatMoney(
                                periodData?.totals?.premieloon_awf || 0
                            )}
                        </TableCell>
                        <TableCell textAlign='right'>
                            {formatMoney(periodData?.totals?.premie_awf || 0)}
                        </TableCell>
                        <TableCell textAlign='center'>
                            {formatNumber(
                                periodData?.totals?.premie_awf_percentage || 0
                            )}{' '}
                            %
                        </TableCell>
                        <TableCell />
                        <TableCell textAlign='right'>
                            {formatMoney(
                                periodData?.totals?.potential_savings || 0
                            )}
                        </TableCell>
                        <TableCell colSpan={2} />
                    </TableRow>
                    <TableRow>
                        <TableCell textAlign='center' colSpan={15}>
                            <ul className='flex flex-row items-center justify-center divide-x-2'>
                                {years?.map(year => (
                                    <li
                                        key={year}
                                        onClick={() => setPickedYear(year)}
                                        className={cn(
                                            'hover:text-primary-600 cursor-pointer px-4',
                                            year == selectedYear &&
                                                'text-primary-500'
                                        )}
                                    >
                                        {year}
                                    </li>
                                ))}
                            </ul>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </>
    );
};

export default ContractCheckPeriodsTable;
